import type { ReactNode } from 'react';

import { CardGrid } from '../../components/CardGrid';
import { EmptyMessage } from '../../components/EmptyMessage';
import { ItemCard } from '../../components/ItemCard';
import { PageHeading } from '../../components/PageHeading';
import { PageSection } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import { space } from '../../theme/tokens';
import { TileArea } from './GolfLandingTemplate.styles';
import type { GolfLandingTemplateProps } from './GolfLandingTemplate.types';

function TileGrid({ children }: { children: ReactNode }) {
  return (
    <TileArea>
      <CardGrid
        $columns={2}
        $mobileColumns={2}
        $columnGapMobile={space['space-300']}
        $rowGapMobile={space['space-400']}
        $columnGapTablet={space['space-400']}
        $rowGapTablet={space['space-500']}
      >
        {children}
      </CardGrid>
    </TileArea>
  );
}

export function GolfLandingTemplate({
  heading,
  subtitle,
  tiles,
  onSelectTile,
  isLoading,
  noResultsLabel = 'Golf is not available at this property.',
  noImageLabel = 'No image',
}: GolfLandingTemplateProps) {
  const pageHeading = <PageHeading title={heading} subtitle={subtitle} centered />;

  if (isLoading) {
    return (
      <PageSection>
        {pageHeading}
        <TileGrid>
          {Array.from({ length: 2 }).map((_, index) => (
            <SkeletonCard key={index} variant="category" />
          ))}
        </TileGrid>
      </PageSection>
    );
  }

  if (tiles.length === 0) {
    return (
      <PageSection>
        {pageHeading}
        <EmptyMessage>{noResultsLabel}</EmptyMessage>
      </PageSection>
    );
  }

  return (
    <PageSection>
      {pageHeading}
      <TileGrid>
        {tiles.map((tile) => (
          <ItemCard
            key={tile.id}
            variant="tile"
            image={{ src: tile.imageUrl, alt: tile.title, placeholderLabel: noImageLabel }}
            title={tile.title}
            ariaLabel={tile.title}
            onSelect={() => onSelectTile(tile.id)}
          />
        ))}
      </TileGrid>
    </PageSection>
  );
}
